import { useEffect, useRef, useState, type ReactNode } from 'react'
import { useApp } from '@/app/AppContext'
import { Icon } from '@/components/Icon'
import '@/styles/create.css'

/**
 * Окно заведения записи (CreateRecord.dc.html): контрагент, лид, сделка.
 *
 * Одна разметка на три формы: шапка с надписью над заголовком, тело `form-grid`, «Ещё
 * поля» за раскрытием и подвал в трёх состояниях — «нельзя, потому что…», «готово» с
 * подсказкой Ctrl ↵ / Esc и «создаём…». Почему нельзя, говорится словами рядом с кнопкой,
 * а не одной погасшей кнопкой: мёртвая кнопка без объяснения выглядит поломкой формы.
 *
 * Список, поверх которого макет рисует окно, здесь не рисуется: заведение живёт на своём
 * адресе (`/deals/new`), и прямая ссылка на него не должна тянуть данные целого списка.
 */
export function CreateModal({
  overline,
  title,
  error,
  children,
  more,
  blockedReason,
  pending,
  submitLabel,
  onCancel,
  onSubmit,
}: {
  overline: string
  title: string
  /** Отказ сервера — его текстом. */
  error?: string | null
  children: ReactNode
  /** Необязательные поля — под раскрытием «Ещё поля». */
  more?: { hint?: string; content: ReactNode }
  /** Причина, по которой создать нельзя; пусто — можно. */
  blockedReason?: string | null
  pending: boolean
  submitLabel: string
  onCancel: () => void
  onSubmit: () => void
}) {
  const { t } = useApp()
  const [moreOpen, setMoreOpen] = useState(false)
  const formRef = useRef<HTMLFormElement>(null)
  const blocked = Boolean(blockedReason)

  useEffect(() => {
    formRef.current?.querySelector<HTMLElement>('input, textarea, select')?.focus()
  }, [])

  const submit = () => {
    if (!blocked && !pending) onSubmit()
  }

  return (
    <div
      className="modal-layer"
      onKeyDown={(event) => {
        if (event.key === 'Escape') {
          event.stopPropagation()
          onCancel()
        } else if (event.key === 'Enter' && (event.ctrlKey || event.metaKey)) {
          event.preventDefault()
          submit()
        }
      }}
    >
      <form
        ref={formRef}
        className="modal modal-lg"
        role="dialog"
        aria-modal="true"
        aria-labelledby="create-title"
        aria-busy={pending || undefined}
        onSubmit={(event) => {
          event.preventDefault()
          submit()
        }}
      >
        <div className="modal-h is-form">
          <div className="stack" style={{ flex: 1 }}>
            <span className="overline">{overline}</span>
            <h1 id="create-title" className="h2" style={{ fontSize: 15, margin: 0 }}>{title}</h1>
          </div>
          <button type="button" className="btn btn-ghost btn-sm btn-icon" aria-label={t('create.close')} onClick={onCancel}>
            <Icon name="close" size="sm" />
          </button>
        </div>

        <div className="modal-b is-form create-body">
          {error && (
            <div className="alert alert-danger" role="alert">
              <Icon name="alert" />
              <span className="fg">{error}</span>
            </div>
          )}
          {children}
          {more && (
            <>
              <button
                type="button"
                className="btn btn-ghost btn-sm create-more"
                aria-expanded={moreOpen}
                aria-controls="create-more"
                onClick={() => setMoreOpen((was) => !was)}
              >
                <Icon name="chevron" size="sm" className={moreOpen ? 'create-chev is-open' : 'create-chev'} />
                {t('create.more')}
                {more.hint && <span className="muted create-more-hint">{more.hint}</span>}
              </button>
              {moreOpen && (
                <div id="create-more" className="form-grid">
                  {more.content}
                </div>
              )}
            </>
          )}
        </div>

        <div className="modal-f create-foot">
          {pending ? (
            <span className="t-sm muted create-foot-note">{t('create.saving.note')}</span>
          ) : blocked ? (
            <span id="create-why" className="flag flag-warning create-foot-note">
              <Icon name="alert" />
              {blockedReason}
            </span>
          ) : (
            <span className="row t-sm muted create-foot-note">
              <span className="kbd">Ctrl ↵</span> {t('create.hint.submit')}
              <span aria-hidden="true">·</span>
              <span className="kbd">Esc</span> {t('create.hint.cancel')}
            </span>
          )}
          <button type="button" className="btn btn-secondary" onClick={onCancel} disabled={pending}>
            {t('card.cancel')}
          </button>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={blocked || pending}
            aria-describedby={blocked ? 'create-why' : undefined}
            aria-busy={pending || undefined}
          >
            {pending && <span className="spinner" aria-hidden="true" />}
            {submitLabel}
          </button>
        </div>
      </form>
    </div>
  )
}

/** Поле формы: подпись, обязательность, подсказка. */
export function CreateField({
  id,
  label,
  required,
  help,
  span,
  children,
}: {
  id?: string
  label: ReactNode
  required?: boolean
  help?: ReactNode
  span?: boolean
  children: ReactNode
}) {
  return (
    <div className={span ? 'field span-2' : 'field'}>
      {/* The asterisk is drawn by CSS (`.is-req`), not a text node: a label is the field's
          accessible name, and "Name *" read aloud or matched by tests is not its name. */}
      {id ? (
        <label className={required ? 'label is-req' : 'label'} htmlFor={id}>{label}</label>
      ) : (
        <span className={required ? 'label is-req' : 'label'}>{label}</span>
      )}
      {children}
      {help && <span className="help">{help}</span>}
    </div>
  )
}

/**
 * Пустое поле уходит **null**, а не пустой строкой.
 *
 * Частичный уникальный индекс `counterparty_inn_kpp_uk` исключает только `inn IS NULL`,
 * поэтому `''` в него попадает: второе физлицо без ИНН падало бы 500 на первом. Пробелы
 * по краям тоже не данные — « 7701» и «7701» не должны быть разными ИНН.
 */
export function blankToNull<T extends object>(body: T): T {
  const result: Record<string, unknown> = {}
  for (const [key, value] of Object.entries(body)) {
    result[key] = typeof value === 'string' ? (value.trim() === '' ? null : value.trim()) : value
  }
  return result as T
}
